<template>
  <div class="chart-data-provider">
    <div v-if="isLoading" style="text-align: center; padding: 40px">
      <a-spin size="large" />
      <div style="margin-top: 8px; color: #999">Loading chart data...</div>
    </div>
    <div v-else>
      <slot :chartData="chartData" :isLoading="isLoading" />
    </div>
  </div>
</template>

<script>
  import AdminService from '../services/admin.service';

  export default {
    name: 'ChartDataProvider',
    props: {
      timePeriod: {
        type: String,
        required: true,
        validator: value => ['24h', '3d', '7d'].includes(value),
      },
      autoFetch: {
        type: Boolean,
        default: true,
      },
      timezone: {
        type: String,
        default: 'utc+8',
        validator: value => ['utc+8', 'local'].includes(value),
      },
    },
    data() {
      return {
        isLoading: false,
        chartData: {
          datasets: [
            {
              label: 'Regular Alerts',
              data: [],
              borderWidth: 2,
              borderColor: '#1890ff',
              pointBorderWidth: 2,
              pointBorderColor: '#1890ff',
              pointBackgroundColor: '#1890ff',
              pointHoverBorderColor: '#1890ff',
              pointHoverRadius: 6,
              fill: false,
            },
            {
              label: 'GenAI Alerts',
              data: [],
              borderWidth: 2,
              borderColor: '#52c41a',
              pointBorderWidth: 2,
              pointBorderColor: '#52c41a',
              pointBackgroundColor: '#52c41a',
              pointHoverBorderColor: '#52c41a',
              pointHoverRadius: 6,
              fill: false,
            },
          ],
          labels: [],
        },
      };
    },
    computed: {
      numberOfDays() {
        switch (this.timePeriod) {
          case '24h':
            return 1;
          case '3d':
            return 3;
          case '7d':
            return 7;
          default:
            return 1;
        }
      },
    },
    mounted() {
      if (this.autoFetch) {
        this.fetchChartData();
      }
    },
    watch: {
      timePeriod: {
        handler() {
          if (this.autoFetch) {
            this.fetchChartData();
          }
        },
        immediate: false,
      },
      timezone: {
        handler() {
          if (this.autoFetch) {
            this.fetchChartData();
          }
        },
        immediate: false,
      },
    },
    methods: {
      convertToUTC8(timestamp) {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          return timestamp;
        }
        
        const utc8Date = new Date(date.getTime() + (8 * 60 * 60 * 1000));
        return utc8Date;
      },

      convertToLocalTime(timestamp) {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) {
          return timestamp;
        }
        return date;
      },

      formatHourDisplay(timestamp, useLocalTime = false) {
        try {
          const date = useLocalTime ? this.convertToLocalTime(timestamp) : this.convertToUTC8(timestamp);
          
          if (typeof timestamp === 'string' && /^\d{1,2}:\d{2}$/.test(timestamp)) {
            return timestamp;
          }
          
          return date.toLocaleTimeString('en-US', { 
            hour: '2-digit', 
            minute: '2-digit', 
            hour12: false,
            timeZone: useLocalTime ? undefined : 'Asia/Singapore'
          });
        } catch (error) {
          console.warn('Error formatting hour:', error);
          return timestamp;
        }
      },

      // Format day for display (e.g., "Jan 15" or "2024-01-15")
      formatDayDisplay(timestamp, useLocalTime = false) {
        try {
          const date = useLocalTime ? this.convertToLocalTime(timestamp) : this.convertToUTC8(timestamp);
          
          if (typeof timestamp === 'string') {
            const parsedDate = new Date(timestamp);
            if (!isNaN(parsedDate.getTime())) {
              const adjustedDate = useLocalTime ? parsedDate : new Date(parsedDate.getTime() + (8 * 60 * 60 * 1000));
              return adjustedDate.toLocaleDateString('en-US', { 
                month: 'short', 
                day: 'numeric',
                timeZone: useLocalTime ? undefined : 'Asia/Singapore'
              });
            }
          }
          
          return date.toLocaleDateString('en-US', { 
            month: 'short', 
            day: 'numeric',
            timeZone: useLocalTime ? undefined : 'Asia/Singapore'
          });
        } catch (error) {
          console.warn('Error formatting day:', error);
          return timestamp;
        }
      },

      async fetchChartData() {
        this.isLoading = true;
        try {
          if (this.timePeriod === '24h') {
            await this.fetch24HourData();
          } else {
            await this.fetchDailyData();
          }
        } catch (error) {
          console.error(`Error fetching chart data for ${this.timePeriod}:`, error);
          this.$message.error(`Failed to load chart data for ${this.timePeriod}`);
        } finally {
          this.isLoading = false;
        }
      },

      async fetch24HourData() {
        try {
          const [regularAlertsResponse, genAiAlertsResponse] = await Promise.all([AdminService.fetch24HoursAlertsGenerated(), AdminService.fetch24HoursAlertsGeneratedByGenAi()]);

          const regularData = regularAlertsResponse.map(item => item.AlertCount);
          const genAiData = genAiAlertsResponse.map(item => item.AlertCount);
          
          // Convert UTC hours based on timezone prop
          const useLocalTime = this.timezone === 'local';
          const labels = regularAlertsResponse.map(item => this.formatHourDisplay(item.hour, useLocalTime));

          this.chartData = {
            datasets: [
              {
                label: 'Regular Alerts',
                data: regularData,
                borderWidth: 2,
                borderColor: '#1890ff',
                pointBorderWidth: 2,
                pointBorderColor: '#1890ff',
                pointBackgroundColor: '#1890ff',
                pointHoverBorderColor: '#1890ff',
                pointHoverRadius: 6,
                fill: false,
              },
              {
                label: 'GenAI Alerts',
                data: genAiData,
                borderWidth: 2,
                borderColor: '#52c41a',
                pointBorderWidth: 2,
                pointBorderColor: '#52c41a',
                pointBackgroundColor: '#52c41a',
                pointHoverBorderColor: '#52c41a',
                pointHoverRadius: 6,
                fill: false,
              },
            ],
            labels: labels,
          };

          this.$emit('chart-data-updated', this.chartData);
        } catch (error) {
          console.error('Error fetching 24-hour chart data:', error);
          throw error;
        }
      },

      async fetchDailyData() {
        try {
          const [regularAlertsResponse, genAiAlertsResponse] = await Promise.all([AdminService.fetchDailyAlertsGenerated(this.numberOfDays), AdminService.fetchDailyAlertsGeneratedByGenAi(this.numberOfDays)]);

          // Parse the response data
          const regularData = regularAlertsResponse.map(item => item.AlertCount);
          const genAiData = genAiAlertsResponse.map(item => item.AlertCount);

          // Convert UTC days based on timezone prop
          const useLocalTime = this.timezone === 'local';
          const labels = regularAlertsResponse.map(item => this.formatDayDisplay(item.day, useLocalTime));

          this.chartData = {
            datasets: [
              {
                label: 'Regular Alerts',
                data: regularData,
                borderWidth: 2,
                borderColor: '#1890ff',
                pointBorderWidth: 2,
                pointBorderColor: '#1890ff',
                pointBackgroundColor: '#1890ff',
                pointHoverBorderColor: '#1890ff',
                pointHoverRadius: 6,
                fill: false,
              },
              {
                label: 'GenAI Alerts',
                data: genAiData,
                borderWidth: 2,
                borderColor: '#52c41a',
                pointBorderWidth: 2,
                pointBorderColor: '#52c41a',
                pointBackgroundColor: '#52c41a',
                pointHoverBorderColor: '#52c41a',
                pointHoverRadius: 6,
                fill: false,
              },
            ],
            labels: labels,
          };

          this.$emit('chart-data-updated', this.chartData);
        } catch (error) {
          console.error(`Error fetching ${this.timePeriod} chart data:`, error);
          throw error;
        }
      },

      // Public method to refresh data
      refresh() {
        return this.fetchChartData();
      },
    },
  };
</script>

<style scoped>
  /* This component doesn't need styles as it doesn't render visible content */
</style>
